import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar as CalendarIcon,
  Plus,
  Edit,
  Trash2,
  X,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Star,
  Clock,
  BarChart3,
} from "lucide-react";
import { holidayAPI } from "../services/employee.service";
import ActivityLogs from "../components/ActivityLogs";
import Reports from "../components/Reports";
import { useAuditLog } from "../hooks/useAuditLog";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isoDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function monthGrid(date) {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  const startDay = start.getDay();
  const daysInMonth = end.getDate();
  const prevMonthEnd = new Date(date.getFullYear(), date.getMonth(), 0).getDate();

  const grid = [];
  for (let i = startDay - 1; i >= 0; i--) {
    const day = prevMonthEnd - i;
    const d = new Date(date.getFullYear(), date.getMonth() - 1, day);
    grid.push(d);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push(new Date(date.getFullYear(), date.getMonth(), d));
  }
  while (grid.length % 7 !== 0) {
    const nextDay = grid.length - (startDay + daysInMonth) + 1;
    const d = new Date(date.getFullYear(), date.getMonth() + 1, nextDay);
    grid.push(d);
  }
  return grid;
}

export default function HolidayCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [weeklyOffs, setWeeklyOffs] = useState([0]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [form, setForm] = useState(emptyHolidayForm());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar');
  const fileRef = useRef(null);
  
  // Holiday Calendar specific activity types
  const activityTypes = [
    "Holiday Created", "Holiday Updated", "Holiday Deleted", 
    "Weekly Off Updated", "Holiday Exported", "Calendar Viewed"
  ];

  // Holiday Calendar specific report types
  const reportTypes = [
    "Holiday Calendar Report", "Weekly Off Report", 
    "Upcoming Holidays Report", "Holiday Statistics Report",
    "Company Holiday Report", "Public Holiday Report"
  ];
  
  // Audit logging hook
  const { logCreate, logUpdate, logDelete, logView, logConfiguration } = useAuditLog('Holiday Calendar');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [holidaysRes, weeklyOffsRes] = await Promise.all([
        holidayAPI.listHolidays(),
        holidayAPI.getWeeklyOffs()
      ]);
      
      console.log('Holiday API Response:', holidaysRes);
      
      let holidaysList = [];
      if (Array.isArray(holidaysRes.data)) {
        holidaysList = holidaysRes.data;
      } else if (holidaysRes.data?.holidays) {
        holidaysList = holidaysRes.data.holidays;
      } else if (Array.isArray(holidaysRes)) {
        holidaysList = holidaysRes;
      }
      console.log('Processed holidays:', holidaysList);
      setHolidays(holidaysList);
      
      let weeklyOffsList = [0];
      if (weeklyOffsRes.data?.weekly_offs) {
        weeklyOffsList = weeklyOffsRes.data.weekly_offs;
      } else if (Array.isArray(weeklyOffsRes.data)) {
        weeklyOffsList = weeklyOffsRes.data;
      }
      setWeeklyOffs(weeklyOffsList);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && drawerOpen) closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const grid = useMemo(() => monthGrid(viewMonth), [viewMonth]);

  const filteredHolidays = useMemo(() => {
    const term = search.trim().toLowerCase();
    return holidays.filter((h) => {
      if (filterType !== "all") {
        const hType = (h.holiday_type || h.type || "").toLowerCase();
        if (filterType === "public" && hType.indexOf("public") === -1) return false;
        if (filterType === "company" && hType.indexOf("company") === -1) return false;
      }
      if (!term) return true;
      return (
        h.title?.toLowerCase().includes(term) ||
        (h.description || "").toLowerCase().includes(term) ||
        (h.holiday_type || h.type || "").toLowerCase().includes(term)
      );
    });
  }, [holidays, search, filterType]);

  const upcoming = useMemo(() => {
    const today = new Date();
    const future = holidays
      .map((h) => {
        const hDate = new Date(h.date);
        if (h.recurring === "yes" || h.recurring === true) {
          const nextYear = today.getFullYear();
          const candidate = new Date(nextYear, hDate.getMonth(), hDate.getDate());
          if (candidate < today) candidate.setFullYear(candidate.getFullYear() + 1);
          return { ...h, nextDate: candidate };
        } else {
          return { ...h, nextDate: new Date(h.date) };
        }
      })
      .filter((x) => x.nextDate >= today)
      .sort((a, b) => a.nextDate - b.nextDate)
      .slice(0, 6);
    return future;
  }, [holidays]);

  function emptyHolidayForm() {
    const todayISO = isoDate(new Date());
    return {
      title: "",
      date: todayISO,
      type: "Public Holiday",
      description: "",
      recurring: false,
    };
  }

  function openCreate() {
    setEditingHoliday(null);
    setForm(emptyHolidayForm());
    setDrawerOpen(true);
    if (fileRef.current) fileRef.current.value = "";
  }

  function openEdit(h) {
    setEditingHoliday(h);
    setForm({
      title: h.title,
      date: h.date,
      type: h.holiday_type || h.type,
      description: h.description || "",
      recurring: h.recurring === "yes" || h.recurring === true,
    });
    setDrawerOpen(true);
    if (fileRef.current) fileRef.current.value = "";
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditingHoliday(null);
    setForm(emptyHolidayForm());
    if (fileRef.current) fileRef.current.value = "";
  }

  async function saveHoliday(e) {
    e?.preventDefault();
    if (!form.title.trim()) {
      alert("Please add a title for the holiday.");
      return;
    }
    if (!form.date) {
      alert("Please select a date.");
      return;
    }

    try {
      const payload = {
        title: form.title,
        date: form.date,
        holiday_type: form.type,
        description: form.description,
        recurring: form.recurring ? "yes" : "no"
      };
      
      if (editingHoliday) {
        await holidayAPI.editHoliday(editingHoliday.id, payload);
      } else {
        await holidayAPI.addHoliday(payload);
      }
      await fetchData();
      closeDrawer();
    } catch (error) {
      console.error('Failed to save holiday:', error.response?.data || error.message);
      alert('Failed to save holiday: ' + (error.response?.data?.detail || error.message));
    }
  }

  async function deleteHoliday(id) {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await holidayAPI.deleteHoliday(id);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      alert('Failed to delete holiday');
    }
  }

  async function toggleWeeklyOff(dayIndex) {
    const newOffs = weeklyOffs.includes(dayIndex)
      ? weeklyOffs.filter((d) => d !== dayIndex)
      : [...weeklyOffs, dayIndex].sort((a, b) => a - b);
    
    try {
      await holidayAPI.updateBulkWeeklyOffs({ weekly_offs: newOffs });
      setWeeklyOffs(newOffs);
    } catch (error) {
      console.error('Failed to update weekly offs:', error);
      alert('Failed to update weekly offs');
    }
  }

  async function exportHolidayICS(h) {
    try {
      const response = await holidayAPI.getSingleHolidayIcs(h.id);
      const blob = new Blob([response.data], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${h.title.replace(/\s+/g, "_")}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export ICS:', error);
      alert('Failed to export ICS');
    }
  }

  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function holidaysForDate(dateObj) {
    const results = [];
    holidays.forEach((h) => {
      if (h.recurring === "yes" || h.recurring === true) {
        const hd = new Date(h.date);
        if (hd.getDate() === dateObj.getDate() && hd.getMonth() === dateObj.getMonth()) {
          results.push(h);
        }
      } else {
        const hDateObj = new Date(h.date);
        if (isSameDay(hDateObj, dateObj)) results.push(h);
      }
    });
    return results;
  }

  function prevMonth() {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }
  function nextMonth() {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }
  function gotoToday() {
    setViewMonth(startOfMonth(new Date()));
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading holidays...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
              <CalendarIcon className="text-teal-600" /> Holiday & Weekly-Off Calendar
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage company holidays and weekly off days.</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white border rounded-lg px-3 py-2 shadow">
              <Search size={16} className="text-gray-400 mr-2" />
              <input className="outline-none w-56" placeholder="Search holidays..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            <select className="border rounded-lg p-2 bg-white" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All</option>
              <option value="public">Public Holidays</option>
              <option value="company">Company Holidays</option>
            </select>

            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700">
              <Plus size={16} /> Add Holiday
            </button>
            
            <button 
              onClick={() => setActiveTab('activity')} 
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition-colors ${
                activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Clock size={16} /> Activity Logs
            </button>
            
            <button 
              onClick={() => setActiveTab('reports')} 
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition-colors ${
                activeTab === 'reports' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BarChart3 size={16} /> Reports
            </button>
            
            <button 
              onClick={() => setActiveTab('calendar')} 
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition-colors ${
                activeTab === 'calendar' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <CalendarIcon size={16} /> Calendar
            </button>
          </div>
        </div>

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <ActivityLogs 
            moduleName="Holiday Calendar" 
            activityTypes={activityTypes} 
          />
        )}
        
        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <Reports 
            moduleName="Holiday Calendar" 
            reportTypes={reportTypes} 
          />
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3 bg-white p-4 rounded-lg shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <button onClick={prevMonth} className="p-2 rounded hover:bg-gray-100"><ChevronLeft size={18} /></button>
                    <div className="text-lg font-semibold">
                      {viewMonth.toLocaleString("default", { month: "long" })} {viewMonth.getFullYear()}
                    </div>
                    <button onClick={nextMonth} className="p-2 rounded hover:bg-gray-100"><ChevronRight size={18} /></button>
                    <button onClick={gotoToday} className="ml-3 text-sm text-gray-600 border px-3 py-1 rounded">Today</button>
                  </div>

                  <div className="text-sm text-gray-500">Weekly offs: {weeklyOffs.map((d) => WEEK_DAYS[d]).join(", ")}</div>
                </div>

                <div className="grid grid-cols-7 text-xs text-gray-500 border-b pb-2">
                  {WEEK_DAYS.map((wd) => (
                    <div key={wd} className="text-center font-medium">{wd}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2 mt-3">
                  {grid.map((d, idx) => {
                    const isCurrentMonth = d.getMonth() === viewMonth.getMonth();
                    const dayHolidays = holidaysForDate(d);
                    const isWeeklyOff = weeklyOffs.includes(d.getDay());
                    return (
                      <div key={idx} className={`p-2 rounded border ${isCurrentMonth ? "bg-white" : "bg-gray-50 text-gray-400"} relative min-h-[80px]`}>
                        <div className="flex items-start justify-between">
                          <div className="text-sm font-medium">{d.getDate()}</div>
                          {isWeeklyOff && <div className="text-xs text-teal-600 font-semibold">Off</div>}
                        </div>

                        <div className="mt-2 space-y-1">
                          {dayHolidays.slice(0, 2).map((h) => (
                            <div key={h.id} className="text-xs bg-red-50 border border-red-100 text-red-700 px-2 py-1 rounded flex justify-between items-center">
                              <div className="truncate max-w-[120px]">{h.title}</div>
                            </div>
                          ))}

                          {dayHolidays.length > 2 && (
                            <div className="text-xs text-gray-500">+{dayHolidays.length - 2} more</div>
                          )}
                        </div>

                        <div className="absolute right-2 bottom-2 flex gap-1 opacity-0 hover:opacity-100 transition-opacity">
                          {dayHolidays.length > 0 && (
                            <button onClick={() => openEdit(dayHolidays[0])} title="Edit" className="p-1 bg-white border rounded text-indigo-600"><Edit size={14} /></button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <aside className="space-y-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2"><Star size={16} /> Weekly Offs</h4>
                    <div className="text-xs text-gray-500">Company-wide</div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {WEEK_DAYS.map((wd, idx) => {
                      const active = weeklyOffs.includes(idx);
                      return (
                        <button key={wd} onClick={() => toggleWeeklyOff(idx)} className={`px-2 py-2 rounded text-sm ${active ? "bg-teal-600 text-white" : "bg-white border text-gray-700"}`}>
                          {wd}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-700">Upcoming</h4>
                    <div className="text-xs text-gray-500">{upcoming.length} items</div>
                  </div>

                  <ul className="mt-3 space-y-3 text-sm">
                    {upcoming.length === 0 && <li className="text-gray-500">No upcoming holidays</li>}
                    {upcoming.map((u) => (
                      <li key={u.id} className="flex justify-between items-start gap-3">
                        <div>
                          <div className="font-medium text-gray-800">{u.title}</div>
                          <div className="text-xs text-gray-500">{u.nextDate ? u.nextDate.toDateString() : new Date(u.date).toDateString()}</div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <button onClick={() => exportHolidayICS(u)} className="text-xs px-2 py-1 border rounded text-gray-700 flex items-center gap-1">
                            <Download size={12} /> ICS
                          </button>
                          <div className="text-xs text-gray-500">{u.recurring === "yes" || u.recurring === true ? "Yearly" : "One-time"}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>

            <div className="mt-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">All Holidays ({filteredHolidays.length})</h4>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="p-2">Title</th>
                        <th className="p-2">Date</th>
                        <th className="p-2">Type</th>
                        <th className="p-2">Recurring</th>
                        <th className="p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHolidays.length > 0 ? (
                        filteredHolidays.map((h) => (
                          <tr key={h.id} className="border-t hover:bg-gray-50">
                            <td className="p-2 font-medium">{h.title}</td>
                            <td className="p-2">{(h.recurring === "yes" || h.recurring === true) ? `${new Date(h.date).toDateString()} (recurring)` : new Date(h.date).toDateString()}</td>
                            <td className="p-2">{h.holiday_type || h.type}</td>
                            <td className="p-2">{h.recurring === "yes" || h.recurring === true ? "Yearly" : "One-time"}</td>
                            <td className="p-2">
                              <div className="flex gap-2">
                                <button onClick={() => openEdit(h)} className="px-2 py-1 border rounded text-indigo-600 flex items-center gap-1 text-xs"><Edit size={14} /> Edit</button>
                                <button onClick={() => deleteHoliday(h.id)} className="px-2 py-1 border rounded text-red-600 flex items-center gap-1 text-xs"><Trash2 size={14} /> Delete</button>
                                <button onClick={() => exportHolidayICS(h)} className="px-2 py-1 border rounded text-gray-700 flex items-center gap-1 text-xs"><Download size={14} /> ICS</button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="p-4 text-center text-gray-500">No holidays match the filter.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer open={drawerOpen} onClose={closeDrawer} title={editingHoliday ? "Edit Holiday" : "Create Holiday"}>
        <form onSubmit={saveHoliday} className="space-y-4">
          <div>
            <label className="text-sm text-gray-700">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded p-2 mt-1" placeholder="Holiday name" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-700">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border rounded p-2 mt-1" required />
            </div>

            <div>
              <label className="text-sm text-gray-700">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border rounded p-2 mt-1">
                <option>Public Holiday</option>
                <option>Company Holiday</option>
                <option>Optional</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-700">Description (optional)</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border rounded p-2 mt-1" />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
              <span className="text-sm text-gray-700">Recurring (every year)</span>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={closeDrawer} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded">{editingHoliday ? "Update" : "Create"}</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}

function Drawer({ open, onClose, title, children }) {
  return (
    <>
      <div className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
      <aside className={`fixed top-0 right-0 bg-white w-full md:w-3/5 lg:w-2/5 xl:w-1/3 h-full z-50 shadow-2xl transform transition-transform ${open ? "translate-x-0" : "translate-x-full"}`} role="dialog" aria-modal="true">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button onClick={onClose} className="p-2 rounded hover:bg-gray-100" aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 72px)" }}>
            {children}
          </div>
        </div>
      </aside>
    </>
  );
}
